// Arithmetic for the private investments, kept out of the components so it can
// be tested on its own.

// Three columns of P/L per investment, each kept as entries: the initial cost
// basis (rounds), what came back (distributions), and the one unrealised mark.
// The rules match the backend's portfolioContribution.ts — keep them together.

const hasMark = (investment) =>
    investment.currentMark !== null && investment.currentMark !== undefined;

/** Every round's cheque, summed: the initial cost basis. */
export const invested = (investment) =>
    (investment.rounds || []).reduce((sum, r) => sum + (r.amountUsd ?? 0), 0);

/** Everything that has come back, held or exited. */
export const distributed = (investment) =>
    (investment.distributions || []).reduce((sum, d) => sum + (d.amountUsd ?? 0), 0);

/** What is still held. An exit holds nothing, whatever mark it last had. */
const remaining = (investment) => (investment.status === 'exited' ? 0 : investment.currentMark);

/**
 * Whether the P/L can be told. Held: it needs a mark. Exited: it needs at
 * least one distribution — an exit with nothing recorded is unknown, and a
 * total loss is a $0 distribution, not silence.
 */
export const isKnown = (investment) =>
    investment.status === 'exited'
        ? (investment.distributions || []).length > 0
        // A linked account's mark is live, but with no cost entered its whole
        // value would read as profit.
        : hasMark(investment) && !(investment.linkedAccountId && (investment.rounds || []).length === 0);

/** The mark plus everything distributed, less what went in. Null when it
 *  cannot be told: unknown, not a total loss. */
export const pnlOf = (investment) =>
    isKnown(investment)
        ? remaining(investment) + distributed(investment) - invested(investment)
        : null;

/** As a percentage of what went in, or null when nothing did. */
export const pnlPctOf = (investment) => {
    const pnl = pnlOf(investment);
    const cost = invested(investment);
    return pnl === null || cost <= 0 ? null : (pnl / cost) * 100;
};

/** "NFT (Digital Fashion)", or whichever half exists. */
export const sectorLabel = (investment) => {
    const { sector, subSector } = investment;
    if (sector && subSector) return `${sector} (${subSector})`;
    return sector || subSector || null;
};

/**
 * A section's headline figures.
 *
 * An investment whose P/L cannot be told (`isKnown`) is left out of the P/L
 * and the multiple — and out of the cost they are measured against, or a
 * missing mark would read as a total loss. `unmarked` says how many were left
 * out. The mark and the distributions are what is recorded, whatever is known.
 */
export function summarize(investments) {
    const known = investments.filter(isKnown);
    const sum = (list, f) => list.reduce((s, i) => s + f(i), 0);
    const knownCost = sum(known, invested);
    const knownValue = sum(known, (i) => remaining(i) + distributed(i));
    const marked = investments.filter((i) => i.status !== 'exited' && hasMark(i));
    const pnl = known.length > 0 ? knownValue - knownCost : null;
    return {
        count: investments.length,
        rounds: sum(investments, (i) => (i.rounds || []).length),
        invested: sum(investments, invested),
        distributed: sum(investments, distributed),
        mark: marked.length > 0 ? sum(marked, (i) => i.currentMark) : null,
        pnl,
        pnlPct: pnl !== null && knownCost > 0 ? (pnl / knownCost) * 100 : null,
        multiple: known.length > 0 && knownCost > 0 ? knownValue / knownCost : null,
        unmarked: investments.length - known.length,
    };
}

/** Sort keys per column. Sector sorts on the MAIN category, then the sub. */
export const sortValue = {
    name: (i) => i.name.toLowerCase(),
    kind: (i) => i.kind,
    sector: (i) => `${(i.sector || '').toLowerCase()}\u0000${(i.subSector || '').toLowerCase()}`,
    invested: (i) => invested(i),
    distributed: (i) => distributed(i),
    currentMark: (i) => i.currentMark,
    pnl: (i) => pnlOf(i),
    comments: (i) => (i.comments || '').toLowerCase(),
};

export function sortInvestments(investments, { key, direction }) {
    const value = sortValue[key] ?? sortValue.name;
    return [...investments].sort((a, b) => {
        const av = value(a);
        const bv = value(b);
        // Missing values sink whichever way the sort runs.
        if (av === null || av === undefined || av === '') return 1;
        if (bv === null || bv === undefined || bv === '') return -1;
        const cmp = typeof av === 'number' ? av - bv : String(av).localeCompare(String(bv));
        return direction === 'asc' ? cmp : -cmp;
    });
}

/** Blank editor rows. */
export const emptyRound = () => ({
    round: '', investedOn: '', amountUsd: '', assetQuantity: '', asset: '', valuationUsd: '', units: '',
});

export const emptyDistribution = () => ({
    description: '', receivedOn: '', amountUsd: '', assetQuantity: '', asset: '', sold: '', soldInto: '',
});

export const emptyInvestment = (status) => ({
    name: '', kind: 'VC', sector: '', subSector: '', status,
    currentMark: '', markQuantity: '', markAsset: '', comments: '',
    rounds: [emptyRound()], distributions: [],
});

/** "25 ETH", or whichever half exists; null when neither does. */
export const assetLabel = (quantity, asset, formatQuantity) => {
    const q = quantity === null || quantity === undefined || quantity === '' ? null : formatQuantity(quantity);
    if (q && asset) return `${q} ${asset}`;
    return q || asset || null;
};

/** An investment as the editor holds it: every field a string. */
export const toDraft = (investment) => ({
    ...investment,
    sector: investment.sector ?? '',
    subSector: investment.subSector ?? '',
    currentMark: investment.currentMark ?? '',
    markQuantity: investment.markQuantity ?? '',
    markAsset: investment.markAsset ?? '',
    comments: investment.comments ?? '',
    rounds: (investment.rounds || []).map((r) => ({
        round: r.round ?? '',
        investedOn: r.investedOn ?? '',
        amountUsd: r.amountUsd ?? '',
        assetQuantity: r.assetQuantity ?? '',
        asset: r.asset ?? '',
        valuationUsd: r.valuationUsd ?? '',
        units: r.units ?? '',
    })),
    distributions: (investment.distributions || []).map((d) => ({
        description: d.description ?? '',
        receivedOn: d.receivedOn ?? '',
        amountUsd: d.amountUsd ?? '',
        assetQuantity: d.assetQuantity ?? '',
        asset: d.asset ?? '',
        // A <select> value: 'Y', 'N', or '' for not recorded.
        sold: d.sold === true ? 'Y' : d.sold === false ? 'N' : '',
        soldInto: d.soldInto ?? '',
    })),
});

/** Commas and dollar signs are typed; neither is part of the number. */
const parseAmount = (v) => {
    if (v === null || v === undefined) return null;
    const clean = String(v).replace(/[$,\s]/g, '');
    if (clean === '') return null;
    const n = Number(clean);
    return Number.isFinite(n) ? n : NaN;
};

const isBlank = (entry) => Object.values(entry).every((v) => String(v ?? '').trim() === '');
const isBad = (n) => Number.isNaN(n) || n < 0;

/**
 * The draft read loosely, for the figures the editor shows as it is typed: an
 * unreadable amount counts as nothing rather than stopping the arithmetic, and
 * a blank entry is not an entry. Saving goes through `fromDraft`, which refuses.
 */
export const draftInvestment = (draft) => {
    const loose = (v) => {
        const n = parseAmount(v);
        return Number.isFinite(n) ? n : null;
    };
    return {
        status: draft.status,
        linkedAccountId: draft.linkedAccountId ?? null,
        currentMark: loose(draft.currentMark),
        rounds: draft.rounds.filter((r) => !isBlank(r)).map((r) => ({ amountUsd: loose(r.amountUsd) })),
        distributions: draft.distributions.filter((d) => !isBlank(d)).map((d) => ({ amountUsd: loose(d.amountUsd) })),
    };
};

/**
 * The draft as the API takes it, or `{ error }` naming the first bad field.
 * An entry left entirely blank is dropped rather than saved as an empty row.
 */
export function fromDraft(draft) {
    if (!draft.name.trim()) return { error: 'name' };
    const currentMark = parseAmount(draft.currentMark);
    const markQuantity = parseAmount(draft.markQuantity);
    if (isBad(currentMark) || isBad(markQuantity)) return { error: 'currentMark' };

    const rounds = [];
    for (const r of draft.rounds) {
        if (isBlank(r)) continue;
        const parsed = {
            round: r.round.trim() || null,
            investedOn: r.investedOn || null,
            amountUsd: parseAmount(r.amountUsd),
            asset: r.asset.trim() || null,
            assetQuantity: parseAmount(r.assetQuantity),
            valuationUsd: parseAmount(r.valuationUsd),
            units: parseAmount(r.units),
        };
        if (['amountUsd', 'assetQuantity', 'valuationUsd', 'units'].some((k) => isBad(parsed[k]))) {
            return { error: 'rounds' };
        }
        rounds.push(parsed);
    }

    const distributions = [];
    for (const d of draft.distributions) {
        if (isBlank(d)) continue;
        const parsed = {
            description: d.description.trim() || null,
            receivedOn: d.receivedOn || null,
            amountUsd: parseAmount(d.amountUsd),
            asset: d.asset.trim() || null,
            assetQuantity: parseAmount(d.assetQuantity),
            sold: d.sold === 'Y' ? true : d.sold === 'N' ? false : null,
            soldInto: d.soldInto.trim() || null,
        };
        if (isBad(parsed.amountUsd) || isBad(parsed.assetQuantity)) return { error: 'distributions' };
        distributions.push(parsed);
    }

    return {
        value: {
            name: draft.name.trim(),
            kind: draft.kind,
            sector: draft.sector.trim() || null,
            subSector: draft.subSector.trim() || null,
            status: draft.status,
            // A linked account's mark is its live value, never stored.
            currentMark: draft.linkedAccountId ? null : currentMark,
            markAsset: draft.markAsset.trim() || null,
            markQuantity,
            comments: draft.comments.trim() || null,
            rounds,
            distributions,
        },
    };
}
